import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableFooter from "components/common/advance-table/AdvanceTableFooter";
import CardDropdown from "components/common/CardDropdown";
import SubtleBadge from "components/common/SubtleBadge";
import { useAdvanceTable } from "hooks";
import useFormattedDate from "hooks/useFormatDate";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React from "react";
import { Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

export const ApplicationCreditsTable = ({ credits, status }) => {
  const navigate = useNavigate();
  const { formatDateTime } = useFormattedDate();
  const columns = [
    {
      accessorKey: "code",
      header: "Code",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "donee",
      header: "Donee",
      cell: ({ getValue }) => {
        const donee = getValue();
        if (!donee) return "NULL";

        const fullName = `${donee.firstName || ""} ${
          donee.lastName || ""
        }`.trim();
        return fullName || donee.nickName || "NULL";
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "amount",
      header: "Amount",
      cell: ({ getValue }) =>
        getValue() ? `$${getValue().toFixed(2)}` : "$0.00",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "balance",
      header: "Balance",
      cell: ({ getValue }) =>
        getValue() ? `$${getValue().toFixed(2)}` : "$0.00",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const value = getValue();
        const statusMap = {
          ACTIVE: { type: "success", label: "Active" },
          REDEEMED: { type: "info", label: "Redeemed" },
          EXPIRED: { type: "danger", label: "Expired" },
          DELETED: { type: "danger", label: "Deleted" },
        };

        const { type, label } = statusMap[value];
        return (
          <SubtleBadge pill bg={type}>
            {label}
          </SubtleBadge>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "fs-9" },
      },
    },
    {
      accessorKey: "issuedAt",
      header: "Issued At",
      cell: ({ getValue }) => {
        return formatDateTime(getValue());
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <CardDropdown drop="start">
            <div className="py-2">
              <Dropdown.Item
                onClick={() => navigate(`/olitask/credits/${item.id}`)}
              >
                View
              </Dropdown.Item>
            </div>
          </CardDropdown>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
  ];

  const table = useAdvanceTable({
    data: credits || [],
    columns: columns,
    sortable: true,
    pagination: true,
    perPage: 10,
  });

  const statusMap = {
    CREDIT_ISSUED: { type: "info", label: "Credit Issued" },
    PENDING: { type: "warning", label: "Pending" },
    APPROVED: { type: "success", label: "Approved" },
    DELETED: { type: "danger", label: "Deleted" },
  };

  const { type, label } = statusMap[status] || {
    type: "secondary",
    label: "Unknown",
  };

  return (
    <>
      <h6 className="fw-bold text-sm">Issued credits</h6>
      {credits && credits.length > 0 ? (
        <AdvanceTableProvider {...table}>
          <AdvanceTable
            headerClassName="bg-200 text-nowrap align-middle"
            rowClassName="btn-reveal-trigger text-nowrap align-middle"
            tableProps={{
              bordered: true,
              striped: true,
              className: "fs-10 mb-0 overflow-hidden",
            }}
          />
          <div className="mt-3">
            <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
          </div>
        </AdvanceTableProvider>
      ) : (
        <div className="text-xs">
          No credits issued for this application.{" "}
          {status === "PENDING" && "(pending approval)"}
        </div>
      )}
    </>
  );
};
